/**
 * Thin imperative wrapper around the `perfectPhraseMachine` actor.
 *
 * The repertoire scene owns one instance and drives it through the loop
 * lifecycle via the `onLoop*` callbacks. The controller hides xstate from the
 * scene and exposes the streak as a plain number for the badge UI.
 *
 * Callback contract (called by the scene's loop controller):
 *   - `onLoopStarted()`   — a fresh loop iteration began playing from the
 *                            bracket start (each count-in → playing transition).
 *   - `onLoopTick(ok)`    — polled every tick while the loop is playing. Pass
 *                            `false` once the running accuracy has dropped below
 *                            100% (any non-green note in the bracket so far).
 *   - `onLoopCompleted(p)`— the cursor reached the bracket end. `p` is the
 *                            strict end-of-loop check (every note green).
 *   - `reset()`           — hard reset (loop-area change, tempo change, long
 *                            pause, or looping disabled).
 *
 * `onStreakChanged` fires whenever `context.streak` changes (including resets to
 * 0), so the scene can keep the badge in sync without polling.
 */

import { createActor, type ActorRefFrom } from 'xstate';

import { perfectPhraseMachine } from './perfectPhraseMachine';
import type { PerfectPhrasePhase } from './perfectPhraseMachine.types';

export default class PerfectPhraseController {
  private readonly actor: ActorRefFrom<typeof perfectPhraseMachine>;
  private readonly onStreakChanged?: (streak: number) => void;
  private lastStreak = 0;

  constructor(onStreakChanged?: (streak: number) => void) {
    this.onStreakChanged = onStreakChanged;
    this.actor = createActor(perfectPhraseMachine);
    this.actor.subscribe((snapshot) => {
      const { streak } = snapshot.context;
      if (streak !== this.lastStreak) {
        console.log(
          `[PPC] machine streak ${this.lastStreak}→${streak} (phase=${snapshot.value as string}, perfectPoints=${snapshot.context.perfectPoints})`,
        );
        this.lastStreak = streak;
        this.onStreakChanged?.(streak);
      }
    });
    this.actor.start();
  }

  /** A fresh loop iteration began playing from the bracket start. */
  onLoopStarted(): void {
    const before = this.getPhase();
    this.actor.send({ type: 'LOOP_STARTED' });
    console.log(
      `[PPC] machine LOOP_STARTED ${before}→${this.getPhase()} streak=${this.getStreak()}`,
    );
  }

  /** Per-tick accuracy poll. Pass `false` once accuracy has dropped below
   *  100% for the current iteration. No-op when accuracy is still perfect. */
  onLoopTick(accuracyPerfect: boolean): void {
    if (!accuracyPerfect) {
      const before = this.getPhase();
      this.actor.send({ type: 'ACCURACY_DROPPED' });
      console.log(
        `[PPC] machine ACCURACY_DROPPED ${before}→${this.getPhase()} streak=${this.getStreak()}`,
      );
    }
  }

  /** The loop reached its end boundary. `finalPerfect` is the strict
   *  end-of-loop check that every note in the bracket was green. */
  onLoopCompleted(finalPerfect: boolean): void {
    const before = this.getPhase();
    const beforeStreak = this.getStreak();
    this.actor.send({ type: 'LOOP_COMPLETED', finalPerfect });
    console.log(
      `[PPC] machine LOOP_COMPLETED finalPerfect=${finalPerfect} ${before}→${this.getPhase()} streak ${beforeStreak}→${this.getStreak()}`,
    );
  }

  /** Hard reset — clears any streak. Loop-area change, tempo change, long
   *  pause (>60s), or looping disabled. */
  reset(): void {
    const before = this.getPhase();
    const beforeStreak = this.getStreak();
    this.actor.send({ type: 'RESET' });
    console.log(
      `[PPC] machine RESET ${before}→${this.getPhase()} streak ${beforeStreak}→${this.getStreak()}`,
    );
  }

  /** Current consecutive perfect-loop streak. */
  getStreak(): number {
    return this.actor.getSnapshot().context.streak;
  }

  /** Current PPC phase (for diagnostics / tests). */
  getPhase(): PerfectPhrasePhase {
    return this.actor.getSnapshot().value as PerfectPhrasePhase;
  }

  destroy(): void {
    this.actor.stop();
  }
}
